export const sunTree = {
  id: 'sun',
  label: 'Sun',
  type: 'G-type main-sequence star (G2V)',
  summary: 'The star at the center of our solar system. 99.86% of the system\'s mass.',
  payload: [
    {
      type: 'description',
      text: 'A nearly perfect sphere of hot plasma, heated to incandescence by nuclear fusion in its core. Every second it converts about 600 million tonnes of hydrogen into helium, releasing energy as light across the solar system. The Sun formed roughly 4.6 billion years ago and is about halfway through its main-sequence lifetime.'
    },
    {
      type: 'stats',
      label: 'At a glance',
      entries: [
        { label: 'Classification',    value: 'G2V (yellow dwarf)' },
        { label: 'Diameter',          value: '1,391,400 km (109 × Earth)' },
        { label: 'Mass',              value: '1.988 × 10³⁰ kg (332,900 × Earth)' },
        { label: 'Surface temp',      value: '5,772 K (photosphere)' },
        { label: 'Core temp',         value: '15.7 million K' },
        { label: 'Luminosity',        value: '3.828 × 10²⁶ W' },
        { label: 'Age',               value: '~4.6 billion years' },
        { label: 'Rotation period',   value: '25 days (equator), 35 days (poles)' }
      ]
    }
  ],
  children: [
    {
      id: 'vital-stats',
      label: 'Vital statistics',
      summary: 'Mass, composition, gravity, output',
      payload: [
        {
          type: 'stats',
          label: 'Physical',
          entries: [
            { label: 'Equatorial radius', value: '695,700 km' },
            { label: 'Volume',            value: '1.41 × 10¹⁸ km³' },
            { label: 'Mean density',      value: '1.408 g/cm³' },
            { label: 'Surface gravity',   value: '274 m/s² (28 g)' },
            { label: 'Escape velocity',   value: '617.7 km/s' }
          ]
        },
        {
          type: 'stats',
          label: 'Composition (by mass)',
          entries: [
            { label: 'Hydrogen', value: '73.46%' },
            { label: 'Helium',   value: '24.85%' },
            { label: 'Oxygen',   value: '0.77%' },
            { label: 'Carbon',   value: '0.29%' },
            { label: 'Iron',     value: '0.16%' },
            { label: 'Neon',     value: '0.12%' }
          ]
        }
      ]
    },
    {
      id: 'structure',
      label: 'Internal structure',
      summary: 'Core, radiative zone, convection zone',
      children: [
        {
          id: 'core',
          label: 'Core',
          summary: 'Where fusion happens',
          payload: [
            { type: 'description', text: 'Extends to about 25% of the Sun\'s radius. Here, temperature and pressure are high enough to fuse hydrogen into helium via the proton-proton chain, producing the energy that powers the star. Despite holding only 2% of the volume, the core contains roughly 34% of the total mass.' },
            {
              type: 'stats',
              entries: [
                { label: 'Temperature', value: '15.7 million K' },
                { label: 'Density',     value: '150 g/cm³ (13 × lead)' },
                { label: 'Pressure',    value: '245 billion atm (2.48 × 10¹¹ bar)' }
              ]
            }
          ]
        },
        {
          id: 'radiative-zone',
          label: 'Radiative zone',
          summary: 'Energy crawls outward as photons',
          payload: [
            { type: 'description', text: 'From ~25% to ~70% of the radius. Energy from the core travels outward by radiative diffusion: a photon takes on the order of 170,000 years to cross this zone because it is absorbed and re-emitted countless times.' }
          ]
        },
        {
          id: 'convection-zone',
          label: 'Convection zone',
          summary: 'Boiling plasma transports energy to the surface',
          payload: [
            { type: 'description', text: 'The outer ~30% of the Sun. Here plasma is cool enough (relatively) that radiation alone can\'t carry heat efficiently, so it boils: hot plasma rises, cools, and sinks. The top of this zone is what we see as the granulated photosphere.' }
          ]
        },
        {
          id: 'photosphere',
          label: 'Photosphere',
          summary: 'The visible surface',
          payload: [
            { type: 'description', text: 'A ~500 km thick layer where the Sun becomes opaque to visible light. Effective temperature 5,772 K. Mottled with granules: the tops of convection cells, each about 1,000 km across and lasting 8–20 minutes.' },
            {
              type: 'stats',
              entries: [
                { label: 'Thickness',   value: '~500 km' },
                { label: 'Temperature', value: '4,400 – 6,600 K' },
                { label: 'Granule size', value: '~1,000 km' }
              ]
            }
          ]
        },
        {
          id: 'chromosphere',
          label: 'Chromosphere',
          summary: 'Thin reddish layer above the photosphere',
          payload: [
            { type: 'description', text: 'A ~2,500 km thick layer. Temperature rises counterintuitively from 4,400 K at its base to about 30,000 K at the top. Normally invisible in white light but appears as a thin red ring during total solar eclipses.' }
          ]
        },
        {
          id: 'corona',
          label: 'Corona',
          summary: 'The superheated outer atmosphere',
          payload: [
            { type: 'description', text: 'The outermost atmosphere, extending millions of kilometers into space. Temperature leaps to 1–3 million K for reasons still not fully understood (the "coronal heating problem"). It is the source of the solar wind, a constant stream of charged particles flowing outward through the solar system.' },
            {
              type: 'stats',
              entries: [
                { label: 'Temperature',  value: '1 – 3 million K' },
                { label: 'Density',      value: '~10⁻¹² × photosphere' },
                { label: 'Solar wind speed', value: '300 – 800 km/s' }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'activity',
      label: 'Solar activity',
      summary: 'Sunspots, flares, CMEs, and the 11-year cycle',
      payload: [
        {
          type: 'description',
          text: 'The Sun\'s magnetic field reverses roughly every 11 years. Near solar maximum, sunspots, flares, and coronal mass ejections spike; at minimum the surface quiets. These cycles drive space weather, which can affect satellites, power grids, and astronauts.'
        },
        {
          type: 'stats',
          label: 'Phenomena',
          entries: [
            { label: 'Sunspot cycle', value: '~11 years (full magnetic cycle: 22)' },
            { label: 'Flare temperature', value: 'up to 20 million K' },
            { label: 'CME mass',      value: '10¹² – 10¹³ kg' },
            { label: 'CME speed',     value: '250 – 3,000 km/s' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'The 1859 Carrington Event was the most powerful geomagnetic storm on record. Telegraph lines sparked, setting fire to some telegraph offices, and auroras were seen as far south as Cuba. A National Academy of Sciences study put the cost of a similar storm today at more than $2 trillion.'
        }
      ]
    },
    {
      id: 'lifecycle',
      label: 'Lifecycle & future',
      summary: 'Past, present, and eventual red giant',
      payload: [
        { type: 'description', text: 'The Sun formed 4.6 billion years ago from a collapsing molecular cloud. It\'s currently on the main sequence, steadily fusing hydrogen in its core. In roughly 5 billion years, hydrogen in the core will be exhausted and the Sun will expand into a red giant, likely engulfing Mercury and Venus and scorching Earth. After shedding its outer layers as a planetary nebula, it will end as a white dwarf.' },
        {
          type: 'stats',
          entries: [
            { label: 'Current age',      value: '4.6 billion years' },
            { label: 'Main sequence left', value: '~5 billion years' },
            { label: 'Red giant phase',  value: '~1 billion years' },
            { label: 'Final state',      value: 'White dwarf' }
          ]
        }
      ]
    },
    {
      id: 'missions',
      label: 'Missions & exploration',
      summary: 'Spacecraft that have watched or touched the Sun',
      payload: [
        {
          type: 'stats',
          entries: [
            { label: 'SOHO',              value: 'Since 1995 (ESA/NASA heliophysics)' },
            { label: 'Parker Solar Probe', value: 'Launched 2018, touched the corona in 2021' },
            { label: 'Solar Orbiter',     value: 'Launched 2020, closest polar views' },
            { label: 'STEREO A/B',        value: 'Launched 2006, stereoscopic Sun imaging' },
            { label: 'SDO',               value: 'Launched 2010, continuous high-res imaging' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'Parker Solar Probe made its record closest approach on 24 December 2024, just 6.1 million km above the Sun\'s surface (about 4% of the Earth-Sun distance), moving at 692,000 km/h. It has matched that pass on later orbits and survives by hiding behind a carbon-composite heat shield.'
        }
      ]
    },
    {
      id: 'references',
      label: 'External references',
      summary: 'Where to dig deeper',
      payload: [
        {
          type: 'links',
          entries: [
            { label: 'NASA fact sheet',     url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/sunfact.html' },
            { label: 'SDO live imagery',    url: 'https://sdo.gsfc.nasa.gov/' },
            { label: 'Parker Solar Probe',  url: 'https://science.nasa.gov/mission/parker-solar-probe/' },
            { label: 'Parker\'s record pass (NASA)', url: 'https://science.nasa.gov/science-research/heliophysics/nasas-parker-solar-probe-makes-history-with-closest-pass-to-sun/' },
            { label: 'NASA: Sun facts',     url: 'https://science.nasa.gov/sun/facts/' },
            { label: 'NASA: Carrington-class storms', url: 'https://science.nasa.gov/science-research/planetary-science/23jul_superstorm/' },
            { label: 'Wikipedia: Sun',      url: 'https://en.wikipedia.org/wiki/Sun' }
          ]
        }
      ]
    }
  ]
};
